import { Component, EventEmitter, Output, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { ToastrService } from 'ngx-toastr';
import { NotificationService } from '../../../../Utils/_services/admin/notifications/notification.service';
import { COUNTRIES } from '../../../../Utils/constants/countries';
import { ISendNotificationReq } from '../../../../Utils/_models/notification.model';

@Component({
  selector: 'app-create-notification-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    ButtonModule
],
  templateUrl: './create-notification-modal.component.html',
  styleUrls: ['./create-notification-modal.component.css']
})
export class CreateNotificationModalComponent implements OnInit {
  @Output() close = new EventEmitter<void>();
  @Output() success = new EventEmitter<void>();

  notificationForm: FormGroup;
  sending = false;

  deliveryTypeOptions = [
    { label: "Push Notification", value: "push" },
    { label: "Email", value: "email" },
    { label: "In-App", value: "in-app" },
    { label: "SMS", value: "sms", disabled: true },
    { label: "Voice Call", value: "voice" },
  ];

  audienceOptions = [
    { label: "All Users", value: "all" },
    { label: "Free Users", value: "free" },
    { label: "Premium Users", value: "premium" },
    { label: "Inactive Users", value: "inactive" },
    { label: "Early Release Users", value: "early_release" },
    { label: "Admin Users", value: "admin" },
    { label: "Single User", value: "single" },
  ];

  countryCodes = COUNTRIES;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private notificationService: NotificationService
  ) {
    this.notificationForm = this.fb.group({
      title: ["", [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      deliveryType: [null, Validators.required],
      message: ["", [Validators.required, Validators.minLength(10), Validators.maxLength(5000)]],
      audience: [null, Validators.required],
      countryCode: ["+1"],
      singleUserContact: [""],
      releaseDate: [""],
    });
  }

  ngOnInit(): void {
    this.notificationForm.get("audience")?.valueChanges.subscribe(() => {
      this.updateValidators();
    });
    this.notificationForm.get("deliveryType")?.valueChanges.subscribe(() => {
      this.updateValidators();
    });
  }

  updateValidators() {
    const audience = this.notificationForm.get("audience")?.value;
    const deliveryType = this.notificationForm.get("deliveryType")?.value;
    const isSms = deliveryType === "sms" || deliveryType === "voice";
    const contactCtrl = this.notificationForm.get("singleUserContact");
    const dateCtrl = this.notificationForm.get("releaseDate");
    const countryCtrl = this.notificationForm.get("countryCode");

    contactCtrl?.clearValidators();
    dateCtrl?.clearValidators();
    countryCtrl?.clearValidators();

    if (audience === "single") {
      if (isSms) {
        contactCtrl?.setValidators([Validators.required, Validators.pattern(/^\+?\d+$/)]);
        countryCtrl?.setValidators([Validators.required]);
      } else {
        contactCtrl?.setValidators([Validators.required]);
      }
    }
    if (audience === "early_release") {
      dateCtrl?.setValidators([Validators.required]);
    }

    contactCtrl?.updateValueAndValidity();
    dateCtrl?.updateValueAndValidity();
    countryCtrl?.updateValueAndValidity();
  }

  isInvalid(field: string): boolean {
    const ctrl = this.notificationForm.get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  get isSingleUser(): boolean {
    return this.notificationForm.get("audience")?.value === "single";
  }

  get isEarlyRelease(): boolean {
    return this.notificationForm.get("audience")?.value === "early_release";
  }

  get isSms(): boolean {
    const dt = this.notificationForm.get("deliveryType")?.value;
    return dt === "sms" || dt === "voice";
  }

  getError(field: string): string {
    const ctrl = this.notificationForm.get(field);
    if (!ctrl || !ctrl.errors) return "";
    if (ctrl.errors["required"]) return `${this.fieldLabel(field)} is required.`;
    if (ctrl.errors["minlength"]) return `${this.fieldLabel(field)} is too short.`;
    if (ctrl.errors["maxlength"]) return `${this.fieldLabel(field)} is too long.`;
    return "Invalid value.";
  }

  private fieldLabel(field: string): string {
    const map: Record<string, string> = {
      title: "Title",
      deliveryType: "Delivery type",
      message: "Message",
      audience: "Audience",
      singleUserContact: this.isSms ? "Mobile Number" : "Email Address",
      releaseDate: "Release date",
    };
    return map[field] ?? field;
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 43) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  sendNotification(): void {
    this.notificationForm.markAllAsTouched();
    if (this.notificationForm.invalid) {
      this.toastr.error("Please fill in all required fields");
      return;
    }

    this.sending = true;
    const { title, deliveryType, message, audience, countryCode, singleUserContact, releaseDate } = this.notificationForm.value;

    const isPhoneType = deliveryType === "sms" || deliveryType === "voice";
    const contactValue = singleUserContact?.trim();

    const body: ISendNotificationReq = {
      title,
      message,
      deliveryType,
      audience,
      ...(audience === "single" && (isPhoneType ? { singleUserPhone: contactValue, countryCode } : { singleUserEmail: contactValue })),
      ...(audience === "early_release" && { releaseDate }),
    };

    this.notificationService.send(body).subscribe({
      next: res => {
        if (res.auth?.success) {
          this.toastr.success(res.msg || "Notification sent successfully");
          this.success.emit();
        } else {
          this.toastr.error(res.msg || "Failed to send notification");
        }
        this.sending = false;
      },
      error: err => {
        this.toastr.error(err.error?.msg || err.message || "Failed to send notification");
        this.sending = false;
      },
    });
  }
}
